"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ADMIN_DESTINATIONS = exports.PHI_ROLES = exports.isRoleName = exports.DEFAULT_ROLE = exports.ROLE_NAMES = exports.isRoleCode = exports.roleCodeGrants = exports.ROLE_CODES = exports.roles = exports.ac = exports.statement = void 0;
exports.scopesHeldBy = scopesHeldBy;
exports.grantsAtScope = grantsAtScope;
// ONE source of truth for roles and permissions (T04 §9.1, T11). Imported by the server guards
// (apps/server/src/plugins/auth.ts) and the web <Can> gate (apps/web/src/features/auth/can.tsx).
const access_1 = require("better-auth/plugins/access");
const access_2 = require("better-auth/plugins/admin/access");
/**
 * Resources and the actions allowed on them. Add a resource here first; TypeScript then
 * forces every role and every check to use only these names.
 */
exports.statement = {
    ...access_2.defaultStatements, // user: [...], session: [...] from the admin plugin
    patient: ["read", "create", "update", "delete", "export"],
    chart: ["read", "create", "update", "sign"],
    audit: ["read"],
    settings: ["read", "update"],
    /** B2 demo resource: GET /api/admin/ping requires admin:["ping"]. */
    admin: ["ping"],
    // `RPM-066`'s catalogue (`01-RPM-SPEC.md`), added to the existing statement — never replaced and
    // never duplicated (N17, D5). Each resource is one the RPM domain acts on, and each action one a
    // route or a later requirement names.
    reading: ["ingest", "read", "correct"],
    alert: ["read", "work", "override_lane", "suppress_override", "complete", "supersede"],
    notification: ["attempt", "read"],
    heldCalls: ["open", "join", "close", "detach"],
    bandSet: ["read", "publish"],
    facilityProfile: ["read", "create", "update", "deactivate"],
    // CR-43: the care units, the facility's routing and its directory, and the summaries. The policy
    // set left with its table.
    careUnit: ["read", "create", "update", "deactivate"],
    routing: ["read", "update", "import"],
    routingContact: ["read", "create", "update", "deactivate"],
    summary: ["read", "send"],
    report: ["read", "export"],
    platform: ["provision_tenant", "provision_practice", "assign_role", "impersonate"],
};
// Explicit alias types keep the generic instantiation in the emitted .d.ts so `admin({ ac, roles })`
// in the server and `adminClient({ ac, roles })` in the web type-check against the compiled package.
exports.ac = (0, access_1.createAccessControl)(exports.statement);
/** Three roles, matching the seeded users admin@ / clinician@ / viewer@chartvn.test (T05 §9.9). */
exports.roles = {
    admin: exports.ac.newRole({
        ...access_2.adminAc.statements, // manage users, sessions, bans, impersonation
        patient: ["read", "create", "update", "delete", "export"],
        chart: ["read", "create", "update", "sign"],
        audit: ["read"],
        settings: ["read", "update"],
        admin: ["ping"],
        bandSet: ["read", "publish"],
        facilityProfile: ["read", "create", "update", "deactivate"],
        careUnit: ["read", "create", "update", "deactivate"],
        routing: ["read", "update", "import"],
        routingContact: ["read", "create", "update", "deactivate"],
        summary: ["read"],
        report: ["read", "export"],
        reading: ["read"],
        alert: ["read"],
        notification: ["read"],
    }),
    clinician: exports.ac.newRole({
        patient: ["read", "create", "update"],
        chart: ["read", "create", "update", "sign"],
        reading: ["ingest", "read", "correct"],
        alert: ["read", "work", "override_lane", "suppress_override", "complete", "supersede"],
        notification: ["attempt", "read"],
        heldCalls: ["open", "join", "close", "detach"],
        bandSet: ["read"],
        facilityProfile: ["read"],
        careUnit: ["read"],
        routing: ["read"],
        routingContact: ["read"],
        summary: ["read", "send"],
    }),
    // Default role for new sign-ups: read-only, no PHI export, no admin.
    viewer: exports.ac.newRole({
        patient: ["read"],
        chart: ["read"],
        reading: ["read"],
        alert: ["read"],
        notification: ["read"],
        bandSet: ["read"],
        facilityProfile: ["read"],
        careUnit: ["read"],
        routing: ["read"],
        routingContact: ["read"],
        summary: ["read"],
    }),
};
/**
 * The sixteen `role_code` values (`CR-33`, `CR-43`, `03-RPM-SCHEMA.md` §4.1.1), in the enum's own order.
 *
 * Declared here as a plain list rather than imported from `db/rpm-enums.ts` because this file is the
 * browser's too, and the enum module brings Drizzle with it. `permissions.test.ts` holds the two
 * lists equal, so they cannot drift.
 */
exports.ROLE_CODES = [
    "PHYSICIAN",
    "NURSE_PRACTITIONER",
    "PHYSICIAN_ASSISTANT",
    "MEDICAL_DIRECTOR",
    "SCRIBE",
    "SCHEDULER",
    "CARE_COORDINATOR",
    "CARE_MANAGER",
    "BILLER_CODER",
    "QUALITY_ANALYST",
    "PRACTICE_ADMINISTRATOR",
    "CUSTOMER_ADMINISTRATOR",
    "READ_ONLY_AUDITOR",
    "INTERFACE_SERVICE",
    "PLATFORM_OPERATOR",
    "NURSE_LEAD",
];
/** What a clinician does in the RPM domain: works readings and alerts, reads configuration. */
const CLINICAL = {
    patient: ["read", "create", "update"],
    chart: ["read", "create", "update", "sign"],
    reading: ["ingest", "read", "correct"],
    alert: ["read", "work", "override_lane", "suppress_override", "complete", "supersede"],
    notification: ["attempt", "read"],
    heldCalls: ["open", "join", "close", "detach"],
    bandSet: ["read"],
    facilityProfile: ["read"],
    // `RPM-066`'s routing table (CR-43): a clinician reads the routing and sends a summary.
    careUnit: ["read"],
    routing: ["read"],
    routingContact: ["read"],
    summary: ["read", "send"],
    // The matrix permits every RPM role to read the source-system register (`source_system` read,
    // `RPM-082`), and `settings: ["read"]` is what that route asks (`RPM-057`, `J-267`).
    settings: ["read"],
};
/** Reading every clinical record and every configuration record, and changing none. */
const READ_ALL = {
    patient: ["read"],
    chart: ["read"],
    reading: ["read"],
    alert: ["read"],
    notification: ["read"],
    bandSet: ["read"],
    facilityProfile: ["read"],
    careUnit: ["read"],
    routing: ["read"],
    routingContact: ["read"],
    summary: ["read"],
    settings: ["read"],
};
/** `RPM-066`'s routing row for both administrators (CR-43): all of routing, and summaries read only. */
const ROUTING_ADMIN = {
    careUnit: ["read", "create", "update", "deactivate"],
    routing: ["read", "update", "import"],
    routingContact: ["read", "create", "update", "deactivate"],
    summary: ["read"],
};
/**
 * `role_assignments.role_code` IS THE AUTHORITY (`RPM-066`, `CR-33`), and this is what each code may
 * do. The server's guard asks it of the ASSIGNMENT that covers the route's scope, so one person with
 * two assignments acts under whichever covers the request (`03-RPM-SCHEMA.md` §10.7).
 *
 * TRANSCRIBED FROM `work/analysis/entity-matrix.json`'s rules, which the corpus states over six roles
 * named as an operator names them (`work/analysis/generate-entity-matrix.mjs`, its `ROLES` list and
 * rules 1-7): the clinical codes are its `nurse` — works alerts, never configures (rule 4);
 * `PRACTICE_ADMINISTRATOR` is the configuring half of its `nurse-manager`, whose clinical half is a
 * SECOND assignment, "never by one role" (rule 4);
 * `CUSTOMER_ADMINISTRATOR` configures and never writes clinically (rule 4); `READ_ONLY_AUDITOR` reads
 * and audits and writes nothing (rule 6); `INTERFACE_SERVICE` creates readings and reads what it
 * classifies against (rule 7); `PLATFORM_OPERATOR` provisions and holds no PHI (rules 1-2, `CR-12`).
 *
 * SIX CODES GET NOTHING, and that is deny by default rather than an omission (`RPM-057`: "Deny by
 * default"). `SCRIBE`, `SCHEDULER`, `CARE_COORDINATOR`, `CARE_MANAGER`, `BILLER_CODER` and
 * `QUALITY_ANALYST` are outside the matrix's RPM slice, and no document grants them an RPM action.
 */
exports.roleCodeGrants = {
    PHYSICIAN: exports.ac.newRole(CLINICAL),
    NURSE_PRACTITIONER: exports.ac.newRole(CLINICAL),
    PHYSICIAN_ASSISTANT: exports.ac.newRole(CLINICAL),
    MEDICAL_DIRECTOR: exports.ac.newRole(CLINICAL),
    SCRIBE: exports.ac.newRole({}),
    SCHEDULER: exports.ac.newRole({}),
    CARE_COORDINATOR: exports.ac.newRole({}),
    CARE_MANAGER: exports.ac.newRole({}),
    BILLER_CODER: exports.ac.newRole({}),
    QUALITY_ANALYST: exports.ac.newRole({}),
    PRACTICE_ADMINISTRATOR: exports.ac.newRole({
        ...READ_ALL,
        audit: ["read"],
        // The matrix lets the nurse manager's configuring half grant roles at practice and facility
        // scope (`role_assignment` create, `RPM-066`; `J-270`). `scopesHeldBy` keeps it there.
        user: ["set-role"],
        facilityProfile: ["read", "create", "update", "deactivate"],
        ...ROUTING_ADMIN,
    }),
    CUSTOMER_ADMINISTRATOR: exports.ac.newRole({
        ...access_2.adminAc.statements, // user and session management: this is the tenant's own administrator
        ...READ_ALL,
        audit: ["read"],
        settings: ["read", "update"],
        admin: ["ping"],
        bandSet: ["read", "publish"],
        facilityProfile: ["read", "create", "update", "deactivate"],
        ...ROUTING_ADMIN,
        report: ["read", "export"],
    }),
    READ_ONLY_AUDITOR: exports.ac.newRole({ ...READ_ALL, audit: ["read"], report: ["read"] }),
    INTERFACE_SERVICE: exports.ac.newRole({
        reading: ["ingest", "read"],
        bandSet: ["read"],
        // The profile carries the zone an ingested reading's local time is read in; the matrix marks
        // `rpm_facility_profile` an ingest read (rule 7, `RPM-028`), and its first read route is `J-276`'s.
        facilityProfile: ["read"],
        settings: ["read"],
    }),
    PLATFORM_OPERATOR: exports.ac.newRole({
        platform: ["provision_tenant", "provision_practice", "assign_role", "impersonate"],
    }),
    // CR-43, OD-19: maintains a facility's contacts and endpoints and nothing else of routing, and
    // writes nothing clinical — a lead nurse holds it as a second assignment beside her clinical code
    // (`RPM-067`). It also reads the facility profile and the existing `settings` resource.
    NURSE_LEAD: exports.ac.newRole({
        careUnit: ["read"],
        routing: ["read"],
        routingContact: ["read", "create", "update", "deactivate"],
        facilityProfile: ["read"],
        settings: ["read"],
    }),
};
/**
 * THE SCOPES EACH CODE MAY BE HELD AT (`J-270`). `work/analysis/generate-entity-matrix.mjs` rule 1:
 * "a platform scope belongs to the platform operator alone", and rule 2: the operator "holds nothing
 * else". `PRACTICE_ADMINISTRATOR` is the configuring half of the matrix's `nurse-manager`, which
 * configures at practice and facility scope and never at tenant scope (its `role_assignment` cells),
 * and `03-RPM-SCHEMA.md` §10.7 holds it at `PRACTICE`. Every other code works at the three scopes a
 * tenant has. An assignment held at a scope its code cannot hold grants nothing (`decideAccess`), and
 * a grant naming one is refused (`POST /api/rpm/v1/admin/role-assignments`).
 */
function scopesHeldBy(code) {
    if (code === "PLATFORM_OPERATOR")
        return ["PLATFORM"];
    if (code === "PRACTICE_ADMINISTRATOR")
        return ["PRACTICE", "CERTIFIED_FACILITY"];
    return ["TENANT", "PRACTICE", "CERTIFIED_FACILITY"];
}
const isRoleCode = (value) => typeof value === "string" && Object.hasOwn(exports.roleCodeGrants, value);
exports.isRoleCode = isRoleCode;
exports.ROLE_NAMES = Object.keys(exports.roles);
exports.DEFAULT_ROLE = "viewer";
// Object.hasOwn, not `in`: `in` walks the prototype chain, so `"toString" in roles` is true and a
// user whose role column held "toString" or "constructor" passed this guard. `roles[that]` then
// returned an Object.prototype method with no .authorize, and requirePermission threw a TypeError
// (HTTP 500) instead of denying with 403. (D-037)
const isRoleName = (value) => typeof value === "string" && Object.hasOwn(exports.roles, value);
exports.isRoleName = isRoleName;
/** Roles whose users may read PHI; the MFA gate applies to them (T12 §9.1 #9). */
exports.PHI_ROLES = ["admin", "clinician", "viewer"];
/**
 * THE ADMINISTRATION SHELL'S DESTINATIONS, IN ITS ORDER (`RPM-092`, `D-1540`, `J-296`), each with
 * the permission and the scope kinds its read route declares. The side navigation lists one only
 * when an assignment at one of those kinds, held at a scope its code can hold (`scopesHeldBy`),
 * grants that READ (`J-649`): a nurse lead reaches the read-only surfaces (`RPM-092` row 5), and a
 * practice administrator, who can never hold `TENANT`, is not offered a tenant-wide screen the
 * server would refuse. `admin/permissions.integration.test.ts` holds each entry equal to the route it
 * names in the server's own permission inventory, so the menu and the guard cannot drift.
 *
 * ONBOARDING (`RPM-061`, `J-295`) takes its slot after Readiness (`D-1540`), and asks what Readiness
 * asks: the tenant's buildings, read at tenant scope.
 */
exports.ADMIN_DESTINATIONS = [
    {
        id: "facilities",
        label: "Facilities",
        href: "/admin/facility-zones",
        read: { facilityProfile: ["read"] },
        scopeKinds: ["TENANT", "CERTIFIED_FACILITY"],
        route: "GET /api/rpm/v1/admin/facilities/:facilityId/profile",
    },
    {
        id: "readiness",
        label: "Readiness",
        href: "/admin/readiness",
        read: { facilityProfile: ["read"] },
        scopeKinds: ["TENANT"],
        route: "GET /api/rpm/v1/admin/readiness",
    },
    {
        id: "onboarding",
        label: "Onboarding",
        href: "/admin/onboarding",
        read: { facilityProfile: ["read"] },
        scopeKinds: ["TENANT"],
        route: "GET /api/rpm/v1/admin/onboarding",
    },
    {
        id: "vital-definitions",
        label: "Vital definitions",
        href: "/admin/vital-definitions",
        read: { bandSet: ["read"] },
        scopeKinds: ["TENANT"],
        route: "GET /api/rpm/v1/admin/vital-definitions",
    },
    {
        id: "band-sets",
        label: "Band sets",
        href: "/admin/band-sets",
        read: { bandSet: ["read"] },
        scopeKinds: ["TENANT", "CERTIFIED_FACILITY"],
        route: "GET /api/rpm/v1/admin/band-sets",
    },
    {
        id: "care-units",
        label: "Care units",
        href: "/admin/care-units",
        read: { careUnit: ["read"] },
        scopeKinds: ["TENANT", "CERTIFIED_FACILITY"],
        route: "GET /api/rpm/v1/admin/facilities/:facilityId/care-units",
    },
    {
        id: "routing",
        label: "Routing",
        href: "/admin/facility-routing",
        read: { routing: ["read"] },
        scopeKinds: ["TENANT", "CERTIFIED_FACILITY"],
        route: "GET /api/rpm/v1/admin/facilities/:facilityId/routing",
    },
    {
        id: "contacts",
        label: "Contacts and endpoints",
        href: "/admin/routing-directory",
        read: { routingContact: ["read"] },
        scopeKinds: ["TENANT", "CERTIFIED_FACILITY"],
        route: "GET /api/rpm/v1/admin/facilities/:facilityId/contacts",
    },
    {
        id: "standing-copies",
        label: "Standing copies",
        href: "/admin/standing-copies",
        read: { routing: ["read"] },
        scopeKinds: ["TENANT"],
        route: "GET /api/rpm/v1/admin/standing-ccs",
    },
    {
        id: "message-content",
        label: "Message content",
        href: "/admin/message-content",
        read: { routing: ["read"] },
        scopeKinds: ["TENANT", "CERTIFIED_FACILITY"],
        route: "GET /api/rpm/v1/admin/notification-attributes",
    },
    {
        id: "routing-import",
        label: "Routing import",
        href: "/admin/routing-import",
        read: { routing: ["import"] },
        scopeKinds: ["TENANT"],
        route: "GET /api/rpm/v1/admin/routing/crosswalk-candidates",
    },
    // The catalogue's two entities `RPM-092`'s destination list does not name: placed before Roles and
    // permissions, which the operator surface makes the last destination (D-1585).
    {
        id: "source-systems",
        label: "Source systems",
        href: "/admin/source-systems",
        read: { settings: ["read"] },
        scopeKinds: ["TENANT"],
        route: "GET /api/rpm/v1/admin/source-systems",
    },
    {
        id: "practice-configurations",
        label: "Practice settings",
        href: "/admin/practice-configurations",
        read: { patient: ["read"] },
        scopeKinds: ["TENANT", "PRACTICE", "CERTIFIED_FACILITY"],
        route: "GET /api/rpm/v1/admin/practice-configurations",
    },
    {
        id: "roles",
        label: "Roles and permissions",
        href: "/admin/roles",
        // The list reads `patient: ["read"]`, the capability every human reader holds and the interface
        // service does not (`role-assignments.routes.ts`); a change asks `user: ["set-role"]`.
        read: { patient: ["read"] },
        scopeKinds: ["TENANT", "PRACTICE", "CERTIFIED_FACILITY"],
        route: "GET /api/rpm/v1/admin/role-assignments",
    },
];
/**
 * Whether any assignment at one of `scopeKinds`, held at a scope its code can hold, grants
 * `permissions` — `decideAccess`'s reach and permission steps with no building named, which is the
 * question a menu asks: is there anywhere this account may do this (`J-649`).
 */
function grantsAtScope(assignments, permissions, scopeKinds) {
    return assignments.some((a) => (0, exports.isRoleCode)(a.roleCode) &&
        scopeKinds.includes(a.scopeKind) &&
        scopesHeldBy(a.roleCode).includes(a.scopeKind) &&
        exports.roleCodeGrants[a.roleCode].authorize(permissions).success);
}
//# sourceMappingURL=permissions.js.map